import { Alert, Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { Client } from 'src/admin/users/types';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { useAdvisors } from '../hooks/useAdvisors';

type Props = {
  conflictingMembers: Client[];
  targetAdvisorUserId?: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export default function ConfirmReassignAdvisorModal({
  conflictingMembers,
  targetAdvisorUserId,
  isLoading,
  onConfirm,
  onCancel,
}: Props) {
  const { advisors } = useAdvisors();

  const advisorName = (advisorUserId?: string) => {
    const advisor = advisors.find(a => a.id === advisorUserId);
    return advisor
      ? formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL)
      : 'another adviser';
  };

  const targetName = advisorName(targetAdvisorUserId);

  return (
    <Modal opened onClose={onCancel} title="Reassign clients to this family's adviser?" centered>
      <Stack gap="md">
        <Text>
          These clients are currently advised by someone else. Creating this family will reassign
          them to {targetName}.
        </Text>
        <List>
          {conflictingMembers.map(member => (
            <List.Item key={member.id}>
              {formatName(member.firstName, member.lastName, NameFormat.NATURAL)} (currently advised
              by {advisorName(member.advisorUserId)})
            </List.Item>
          ))}
        </List>
        <Alert color="yellow">Their previous adviser will no longer be able to see them.</Alert>
        <Group justify="flex-end">
          <Button variant="outline-black" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="black" onClick={onConfirm} loading={isLoading}>
            Reassign and create family
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
